import { ClarityUnmaskDirective } from 'app/shared/directives/clarity-unmask.directive';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '@service-features/auth.service';
import { ApiErrorAdapter } from '@shared-services/handlers/api-error-adapter.service';
import { passwordMatchValidator } from '@validators/password-match.validator';
import { firstValueFrom } from 'rxjs';
import { InlineSpinnerComponent } from '@shared-components/inline-spinner/inline-spinner.component';

export enum PasswordResetState {
  READY = 'ready',
  MISSING = 'missing',
  LOADING = 'loading',
  SUCCESS = 'success',
  INVALID = 'invalid',
  EXPIRED = 'expired',
  USED = 'used',
  ERROR = 'error',
}

@Component({
  selector: 'gd-reset-password',
  standalone: true,
  imports: [
    InlineSpinnerComponent,
    ClarityUnmaskDirective,
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit {
  readonly states = PasswordResetState;
  readonly passwordValidators = [
    Validators.required,
    Validators.minLength(6),
    Validators.pattern(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/,
    ),
  ];
  readonly form = new FormGroup(
    {
      password: new FormControl('', {
        nonNullable: true,
        validators: this.passwordValidators,
      }),
      confirmPassword: new FormControl('', {
        nonNullable: true,
        validators: this.passwordValidators,
      }),
    },
    { validators: passwordMatchValidator() },
  );
  state = PasswordResetState.READY;
  successMessage = '';
  errorMessage = '';
  private token = '';
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private errors: ApiErrorAdapter,
  ) {}
  ngOnInit(): void {
    void this.captureToken();
  }
  async submit(): Promise<void> {
    if (
      !this.token ||
      this.form.invalid ||
      this.state === PasswordResetState.LOADING
    ) {
      this.form.markAllAsTouched();
      return;
    }
    this.state = PasswordResetState.LOADING;
    const value = this.form.getRawValue();
    try {
      const response = await firstValueFrom(
        this.auth.resetPassword(
          this.token,
          value.password,
          value.confirmPassword,
        ),
      );
      this.token = '';
      this.successMessage = response.payload;
      this.state = PasswordResetState.SUCCESS;
    } catch (error: unknown) {
      const apiError = this.errors.from(error);
      this.errorMessage = apiError.message;
      this.state =
        apiError.code === 'PASSWORD_RESET_TOKEN_EXPIRED'
          ? PasswordResetState.EXPIRED
          : apiError.code === 'PASSWORD_RESET_TOKEN_ALREADY_USED'
            ? PasswordResetState.USED
            : apiError.code === 'PASSWORD_RESET_TOKEN_INVALID'
              ? PasswordResetState.INVALID
              : PasswordResetState.ERROR;
    }
  }
  private async captureToken(): Promise<void> {
    this.token = this.route.snapshot.queryParamMap.get('token') ?? '';
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
    if (!this.token) this.state = PasswordResetState.MISSING;
  }
}
